import { type LatLngBounds } from '~/_shared/types/latLng';
import { type Geometry } from '~/_shared/types/polygon/polygon.types';
import {
  apiGet, apiPost,
} from '~/_shared/utils/api/api.helpers';
import { type BoundaryItemResponseBase } from '~/store/boundaries/boundaries.repository';
import {
  type BoundaryFilterBoundariesRequest, type BoundaryFilterRequest,
} from '../../spreadsheet/filter/boundary/spreadsheetFilterBoundary.types';
import {
  type BoundaryTerritoryFilterGroups, type BoundaryTerritoryFilterRequest,
} from '../../spreadsheet/filter/boundaryTerritory/spreadsheetFilterBoundaryTerritory.types';
import { type PolygonFilterRequest } from '../../spreadsheet/filter/polygon/spreadsheetPolygonFilter.types';
import { type RadiusFilterRequest } from '../../spreadsheet/filter/radius/spreadsheetFilterRadius.types';
import {
  type BoundaryGroupId, type BoundaryId,
} from '../boundaries/boundaryIdentifier.type';

export type MapBoundaryQueryRequestItem = {
  zoom_level: number;
  boundary_group_id: number;
  bounding_box: LatLngBounds;
  continuation_token?: string;
};

type MapBoundaryQueryRequest = {
  map_id: number;
  query: MapBoundaryQueryRequestItem[];
};

export type MapBoundaryQueryItemResponse = BoundaryItemResponseBase & {
  label_bounding_box: LatLngBounds | null;
  geometry: Geometry;
};

export type BoundaryGroupObject = {
  [boundaryGroupId: number]: BoundaryGroupBoundaryObject;
};
export type BoundaryGroupBoundaryObject = {
  [boundary_id: number]: MapBoundaryQueryItemResponse;
};

export type MapBoundaryGeometryResponse = {
  geometries: {
    zoom_level: number;
  } & BoundaryGroupObject;
};

export type MapBoundaryQueryResponse = {
  list: [{
    continuation_token?: string;
    zoom_level: number;
    done: boolean;
  } & BoundaryGroupObject];
};

type AreaQueryBoundaryTerritoryFilterRequest = Omit<BoundaryTerritoryFilterRequest, 'boundary_territory_groups'> & Readonly<{
  boundary_territory_groups: readonly (BoundaryTerritoryFilterGroups & { column_name?: string })[];
}>;

type AreaQueryBoundaryFilterRequest = Omit<BoundaryFilterRequest, 'boundaries'> & {
  boundaries: {
    [boundaryGroupId: BoundaryGroupId]: BoundaryFilterBoundariesRequest[BoundaryGroupId] & { column_name?: string };
  };
};

export type AreaQueryFilters = {
  boundary_territory_filter?: AreaQueryBoundaryTerritoryFilterRequest;
  boundary_filter?: AreaQueryBoundaryFilterRequest;
  polygon_filter?: PolygonFilterRequest;
  radius_filter?: RadiusFilterRequest;
};

export enum MapQueryStatusResponse {
  Started = '1_STARTED',
  DataInitialized = '2_DATA_INITIALIZED',
  GeometryPrepared = '3_GEOMETRY_PREPARED',
  BoundariesFiltered = '4_BOUNDARIES_FILTERED',
  ResultFormatted = '5_RESULT_FORMATTED',
  Finished = '6_FINISHED',
}

export type MapBoundaryAreaQueryRequest = {
  map_id: number;
  boundary_group_id: number;
  format: 'json' | 'xlsx' | 'csv' | 'tsv';
  as_text?: boolean;
  overlap_type: 'partial' | 'contains';
  name_overrides?: { [key: BoundaryId]: string };
  exclude_artificial?: boolean;
} & AreaQueryFilters;

type MapBoundaryAreaQueryBoundaryResponse = {
  boundary_id: number;
  codename: string;
  display_name: string;
  meta_data: {
    settings: Record<string, unknown>;
    state_code: string;
  };
};

export type MapBoundaryAreaQueryStartAsyncResponse = Readonly<{
  hash: string;
}>;

type MapBoundaryAreaQueryResponse = {
  boundary_group_id: number;
  boundaries: MapBoundaryAreaQueryBoundaryResponse[];
} | {
  download: string | null;
} | {
  data: string | null;
};

export const getMapBoundaryQuery = (
  clientId: number, request: MapBoundaryQueryRequest
): Promise<{ data: MapBoundaryQueryResponse }> => {
  const requestUrl = `/api/clients/${clientId}/boundary/query`;

  return apiPost(requestUrl, request);
};

export const getMapBoundaryGeometry = (
  clientId: number, boundaryGroupsIds: number[],
): Promise<MapBoundaryGeometryResponse[]> => {
  const getRequestUrl = (boundaryGroupId: number) => `/api/clients/${clientId}/boundary-group/${boundaryGroupId}/geometry`;

  const promiseArray = boundaryGroupsIds.map((boundaryGroupId) => apiGet(getRequestUrl(boundaryGroupId)));
  return Promise.all(promiseArray);
};

export const getMapBoundaryAreaQuery = (
  clientId: number, request: MapBoundaryAreaQueryRequest
): Promise<MapBoundaryAreaQueryResponse> => {
  const requestUrl = `/api/clients/${clientId}/boundary/area-query`;

  return apiPost(requestUrl, request);
};

export const getMapBoundaryAreaQueryAsyncStart = (
  clientId: number, request: MapBoundaryAreaQueryRequest
): Promise<MapBoundaryAreaQueryStartAsyncResponse> => {
  const requestUrl = `/api/clients/${clientId}/boundary/area-query/async`;

  return apiPost(requestUrl, request);
};

export const getMapBoundaryAreaQueryAsyncResult = (
  clientId: number, hash: string,
): Promise<MapBoundaryAreaQueryResponse> => {
  const requestUrl = `/api/clients/${clientId}/boundary/area-query/async/result`;

  return apiPost(requestUrl, { hash });
};
